import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, PaperPlaneTilt, FilePdf, Trash, Warning } from "@phosphor-icons/react";
import ItensMedicao, { totalMedicao } from "@/components/medicao/ItensMedicao";
import ItensResumoTable from "@/components/medicao/ItensResumoTable";
import FotosManager from "@/components/medicao/FotosManager";
import DocumentosManager from "@/components/medicao/DocumentosManager";
import IAMedicao from "@/components/medicao/IAMedicao";
import Timeline from "@/components/medicao/Timeline";
import { StatusBadge, errMsg, fmtBRL, fmtDate, openPdf } from "@/lib/medicoes";

const TEXT_FIELDS = [
  ["memoria_calculo", "Memória de cálculo das quantidades", 6],
  ["descricao_servicos", "Descrição dos serviços executados", 4],
  ["justificativas", "Justificativas técnicas", 3],
  ["observacoes_engenharia", "Observações da equipe de engenharia", 3],
  ["referencias_documentos", "Referências a projetos, plantas e especificações", 2],
];
const ID_FIELDS = [
  ["periodo_inicio", "Início do período", "date"], ["periodo_fim", "Fim do período", "date"],
  ["referencia", "Referência (mês/etapa)"], ["frente_servico", "Obra / etapa / lote / frente"],
  ["responsavel_preenchimento", "Responsável pelo preenchimento"], ["engenheiro_nome", "Engenheiro responsável"],
  ["engenheiro_registro", "Registro profissional (CREA/CAU)"],
];

const Section = ({ title, children, testid }) => (
  <section className="tce-card p-6" data-testid={testid}>
    <div className="uppercase-label !text-[#FFDF00] mb-4">{title}</div>{children}
  </section>
);

function Pendencias({ pendencias, editable, respostas, setRespostas }) {
  if (!pendencias?.length) return null;
  return (
    <Section title="Pendências apontadas pela fiscalização" testid="pendencias-section">
      <div className="space-y-3">
        {pendencias.map((p, i) => (
          <div key={p.id} className={`rounded border p-3 ${p.resolvida ? "border-[#334155] opacity-70" : "border-[#F97316]/60 bg-[#F97316]/5"}`} data-testid={`pendencia-${i}`}>
            <div className="text-sm flex items-start gap-2"><Warning size={14} className="text-[#F97316] mt-0.5 shrink-0" />{p.texto}</div>
            <div className="text-[10px] text-[#64748B] mt-1">Versão {p.versao} · {p.criada_por} {p.resolvida && "· resolvida"}</div>
            {editable && !p.resolvida ? (
              <textarea data-testid={`pendencia-resposta-${i}`} rows={2} value={respostas[p.id] ?? p.resposta ?? ""}
                        onChange={(e) => setRespostas((s) => ({ ...s, [p.id]: e.target.value }))}
                        placeholder="Descreva a correção realizada…" className="tce-input w-full px-3 py-2 rounded-md text-xs mt-2" />
            ) : p.resposta && <div className="text-xs text-[#22C55E] mt-2">Resposta: {p.resposta}</div>}
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function MedicaoEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [valores, setValores] = useState({});
  const [respostas, setRespostas] = useState({});
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState("");

  const load = useCallback(async (resetForm) => {
    const { data } = await api.get(`/medicoes/${id}`);
    setD(data);
    if (resetForm) {
      const m = data.medicao;
      setForm(Object.fromEntries([...ID_FIELDS, ...TEXT_FIELDS, ["observacoes"]].map(([k]) => [k, m[k] || ""])));
      setValores(Object.fromEntries((m.itens || []).map((i) => [i.numero_item, { q: String(i.quantidade_periodo), obs: i.observacao || "" }])));
    }
  }, [id]);
  useEffect(() => { load(true).catch((e) => toast.error(errMsg(e, "Medição indisponível"))); }, [load]);

  const m = d?.medicao;
  const editable = m && ["rascunho", "devolvida"].includes(m.status);
  const total = useMemo(() => (d ? totalMedicao(d.planilha, valores) : 0), [d, valores]);

  const payload = () => ({
    ...form,
    itens: Object.entries(valores).filter(([, v]) => parseFloat(v.q) > 0)
      .map(([n, v]) => ({ numero_item: parseInt(n, 10), quantidade_periodo: parseFloat(v.q), observacao: v.obs || "" })),
    respostas_pendencias: respostas,
  });

  const salvar = async (silent) => {
    if (form.periodo_inicio && form.periodo_fim && form.periodo_fim < form.periodo_inicio) {
      toast.error("O fim do período não pode ser anterior ao início"); throw new Error("periodo");
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/fornecedor/medicoes/${id}`, payload());
      if (!silent) {
        if (data.alertas?.length) data.alertas.forEach((a) => toast.warning(a));
        else toast.success("Rascunho salvo");
      }
      await load(false);
      return data;
    } catch (e) { if (!silent || e.message !== "periodo") toast.error(errMsg(e)); throw e; }
    finally { setSaving(false); }
  };

  const enviar = async () => {
    if (!window.confirm("Confirmar o envio formal da medição à fiscalização?\n\nApós o envio, a medição não poderá ser alterada, salvo se for devolvida para correção.")) return;
    try {
      await salvar(true);
      await api.post(`/fornecedor/medicoes/${id}/enviar`);
      toast.success("Medição enviada para análise da fiscalização");
      await load(true);
    } catch (e) { if (e?.response) toast.error(errMsg(e)); }
  };

  const excluir = async () => {
    if (!window.confirm("Excluir este rascunho? Esta ação não pode ser desfeita.")) return;
    try { await api.delete(`/fornecedor/medicoes/${id}`); toast.success("Rascunho excluído"); nav(`/fornecedor/contratos/${m.contract_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const usarIA = (tipo, texto) => {
    if (tipo === "memoria") {
      const [mem, desc] = texto.split(/##\s*Descrição dos serviços/i);
      setForm((f) => ({ ...f, memoria_calculo: mem.replace(/##\s*Memória de cálculo/i, "").trim(), descricao_servicos: (desc || f.descricao_servicos || "").trim() }));
    } else {
      setForm((f) => ({ ...f, observacoes_engenharia: `${f.observacoes_engenharia ? `${f.observacoes_engenharia}\n\n` : ""}${texto}` }));
    }
    toast.success("Texto inserido no formulário — revise antes de salvar");
  };

  if (!d) return <div className="text-[#94A3B8]">Carregando…</div>;
  const fotos = d.arquivos.filter((a) => a.tipo === "foto");
  const docs = d.arquivos.filter((a) => a.tipo === "documento");
  const inp = "tce-input w-full px-3 py-2.5 rounded-md text-sm";

  return (
    <div className="space-y-6 max-w-6xl pb-28" data-testid="medicao-editor">
      <Link to={`/fornecedor/contratos/${m.contract_id}`} className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#FFDF00]"><ArrowLeft size={14} /> Contrato {m.contract_numero}</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="uppercase-label">Medição de obra · Contrato {m.contract_numero}</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2" data-testid="medicao-numero">Medição nº {m.numero} <span className="text-lg text-[#94A3B8]">v{m.versao}</span></h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-3xl">{m.contract_objeto}</p>
        </div>
        <StatusBadge status={m.status} valorPago={m.valor_pago} testid="medicao-status" />
      </div>
      {!editable && <div className="tce-card p-4 text-sm text-[#3B82F6]" data-testid="medicao-bloqueada">Esta versão foi enviada formalmente e está bloqueada para edição. Acompanhe a tramitação abaixo.</div>}
      {editable && d.alertas?.length > 0 && (
        <div className="tce-card p-4 border-[#EF4444]/60" data-testid="medicao-alertas">
          {d.alertas.map((a, i) => <div key={i} className="text-sm text-[#EF4444] flex gap-2"><Warning size={14} className="mt-0.5" />{a}</div>)}
        </div>
      )}

      <Pendencias pendencias={m.pendencias} editable={editable} respostas={respostas} setRespostas={setRespostas} />

      <Section title="1. Identificação da medição" testid="sec-identificacao">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ID_FIELDS.map(([k, l, t]) => (
            <div key={k} className={k === "frente_servico" ? "lg:col-span-2" : ""}>
              <label className="uppercase-label !text-[10px] block mb-1.5">{l}</label>
              <input data-testid={`med-${k}`} type={t || "text"} disabled={!editable} value={form[k] || ""}
                     onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} />
            </div>
          ))}
        </div>
        <label className="uppercase-label !text-[10px] block mb-1.5 mt-3">Observações gerais</label>
        <textarea data-testid="med-observacoes" rows={2} disabled={!editable} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className={inp} />
      </Section>

      <Section title="2. Serviços executados (planilha contratual)" testid="sec-itens">
        {editable ? (
          <>
            <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Filtrar itens da planilha…" data-testid="med-itens-filtro" className={`${inp} mb-4`} />
            <ItensMedicao planilha={d.planilha} valores={valores} setValores={setValores} filtro={filtro} />
          </>
        ) : <ItensResumoTable itens={m.itens} anteriores={d.anteriores} />}
      </Section>

      <Section title="3. Memória de cálculo e justificativas" testid="sec-memoria">
        <div className="space-y-3">
          {TEXT_FIELDS.map(([k, l, rows]) => (
            <div key={k}>
              <label className="uppercase-label !text-[10px] block mb-1.5">{l}</label>
              <textarea data-testid={`med-${k}`} rows={rows} disabled={!editable} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${inp} font-mono text-xs`} />
            </div>
          ))}
        </div>
      </Section>

      {editable && <IAMedicao medicaoId={id} tipos={[["memoria", "Gerar memória de cálculo"], ["revisao", "Conferir antes de enviar"]]} onUse={usarIA} beforeRun={() => salvar(true)} />}

      <Section title="4. Relatório fotográfico" testid="sec-fotos">
        <FotosManager medicaoId={id} fotos={fotos} editable={editable} planilha={d.planilha} onChange={() => load(false)} />
      </Section>

      <Section title="5. Documentos complementares" testid="sec-docs">
        <DocumentosManager medicaoId={id} docs={docs} editable={editable} onChange={() => load(false)} />
      </Section>

      {m.pareceres?.length > 0 && (
        <Section title="Pareceres da fiscalização" testid="sec-pareceres">
          {m.pareceres.map((p, i) => <div key={i} className="text-sm border-b border-[#334155]/50 py-2"><div className="text-[10px] text-[#64748B]">{p.por_nome || p.por} · {fmtDate(p.em)} · {p.acao}</div><div className="whitespace-pre-wrap">{p.texto}</div></div>)}
        </Section>
      )}

      <Section title="Linha do tempo" testid="sec-timeline"><Timeline items={m.timeline} /></Section>

      <div className="fixed bottom-0 left-0 md:left-64 right-0 z-30 bg-[#0F172A]/95 backdrop-blur border-t border-[#334155] px-4 md:px-10 py-3 flex flex-wrap items-center gap-2" data-testid="medicao-actions">
        <div className="mr-auto">
          <div className="uppercase-label !text-[10px]">Valor da medição</div>
          <div className="font-mono text-xl text-[#FFDF00]" data-testid="medicao-total">{fmtBRL(editable ? total : m.valor_total)}</div>
        </div>
        <button onClick={() => openPdf(`/medicoes/${id}/relatorio-fotografico`)} data-testid="btn-relatorio-pdf"
                className="px-4 py-2.5 rounded-md border border-[#334155] hover:border-[#FFDF00] text-sm flex items-center gap-2"><FilePdf size={16} /> Relatório fotográfico</button>
        {editable && m.status === "rascunho" && !m.enviada_em && (
          <button onClick={excluir} data-testid="btn-excluir-rascunho" className="px-4 py-2.5 rounded-md border border-[#EF4444]/40 text-[#EF4444] text-sm flex items-center gap-2"><Trash size={16} /> Excluir</button>
        )}
        {editable && (
          <>
            <button onClick={() => salvar(false).catch(() => {})} disabled={saving} data-testid="btn-salvar-rascunho"
                    className="px-4 py-2.5 rounded-md border border-[#FFDF00]/60 text-[#FFDF00] text-sm flex items-center gap-2 disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "Salvando…" : "Salvar rascunho"}</button>
            <button onClick={enviar} disabled={saving} data-testid="btn-enviar-medicao"
                    className="px-5 py-2.5 rounded-md bg-[#009C3B] text-white hover:bg-[#00B347] text-sm font-medium flex items-center gap-2 disabled:opacity-50"><PaperPlaneTilt size={16} /> {m.status === "devolvida" ? "Reenviar" : "Enviar medição"}</button>
          </>
        )}
      </div>
    </div>
  );
}
